
import { defineComponent, ref, computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import DynamicGroupForm from '@/components/form/common/DynamicGroupForm.vue'
import { ElForm } from 'element-plus'
import { GroupInstance, GroupField } from '@/components/form/common/DynamicFormModel'

export default defineComponent({

  components: {
    DynamicGroupForm
  },

  props: {
    formModel: {
      type: Object,
      default: () => {
        return null
      }
    },
    formStruct: {
      type: Array,
      default: () => [] as any
    },
    formRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    groupsModel: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => []
    },
    groupsStruct: {
      type: Array as () => GroupField[],
      default: () => [] as any
    },
    groupsRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    submitButton: {
      type: String,
      default: 'common.submit'
    },
    resetButton: {
      type: String,
      default: 'common.reset'
    },
    formWidth: {
      type: Number,
      default: 850
    },
    labelWidth: {
      type: String,
      default: '150px'
    },
    groupLabelWidth: {
      type: String,
      default: '100px'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    defaultGroup: {
      type: Boolean,
      default: false
    },
    permitAddGroup: {
      type: Boolean,
      default: true
    }
  },

  emits: ['submit', 'reset', 'groups-change'],

  setup(props, context) {
    const { t } = useI18n()
    const cFormRef = ref(ElForm)
    const fM = ref(props.formModel || {})
    const fS = ref(props.formStruct)
    const fR = ref(props.formRules)

    // 动态组相关状态
    let formGroupId = 0

    // 从父组件获取字段配置
    const groupFields = computed<GroupField[]>(() => {
      return props.groupsStruct || []
    })
    const groupFormRules = computed(() => {
      return props.groupsRules || {}
    })
    // 存放动态组的 form refs
    const groupFormRefs = ref<any[]>([])

    // 子组件调用时执行
    const setGroupFormRef = (groupIndex: number, el: any) => {
      if (el) {
        groupFormRefs.value[groupIndex] = el
      } else {
        // 防止 v-for 删除后还保留旧 ref
        groupFormRefs.value.splice(groupIndex, 1)
      }
    }

    // 创建新的信息组
    const createGroup = (model?: Record<string, any>): GroupInstance => {
      formGroupId++

      const defaultData: Record<string, any> = {}

      // 确保遍历 groupFields 并初始化所有属性
      groupFields.value.forEach(field => {
        defaultData[field.prop] = model?.[field.prop] ?? ''
      })

      return {
        id: formGroupId,
        model: defaultData
      }
    }
    const formGroupsRefs = ref<any[]>([])
    const formGroups = ref<GroupInstance[]>(
      props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model))
        : props.defaultGroup
          ? [createGroup()]
          : []
    )

    const addGroup = () => {
      const newGroup = createGroup()
      formGroups.value.push(newGroup)
      context.emit('groups-change', formGroups.value.map(group => group.model))
    }

    const removeGroup = (index: number) => {
      if (!props.defaultGroup || formGroups.value.length > 1) {
        formGroups.value.splice(index, 1)
        formGroupsRefs.value.splice(index, 1)
        context.emit('groups-change', formGroups.value.map(group => group.model))
      }
    }

    // const submitEmit = () => {
    //   (cFormRef.value as typeof ElForm).validate((valid: any) => {
    //     if (valid) {
    //       const submitData = {
    //         ...fM.value,
    //         model: formGroups.value.map(group => group.model)
    //       }
    //       context.emit('submit', submitData)
    //       console.log('提交数据:', JSON.stringify(submitData))
    //     }
    //   })
    // }
    const submitEmit = async() => {
      // 校验主表单
      const mainValid = await cFormRef.value?.validate().catch(() => false)
      // if (!mainValid) {
      //   console.warn('主表单校验失败')
      //   return
      // }

      // 校验所有动态子表单
      const groupValid = await Promise.all(
        groupFormRefs.value.map(form =>
          form?.validate().catch(() => false)
        )
      )
      if (groupValid.includes(false) || !mainValid) {
        return
      }

      // 所有表单都通过了
      const submitData = {
        ...fM.value,
        model: formGroups.value.map(group => group.model)
      }
      context.emit('submit', submitData)
    }

    const resetEmit = () => {
      (cFormRef.value as typeof ElForm).resetFields()
      formGroups.value = []
      formGroupsRefs.value = []

      if (props.defaultGroup) {
        addGroup()
      }
      // 重置动态表单组到初始状态
      formGroups.value = props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model))
        : props.defaultGroup
          ? [createGroup()]
          : []
      context.emit('reset')
    }

    const updateGroupModel = (groupIndex: number, newModel: Record<string, any>) => {
      if (formGroups.value[groupIndex]) {
        formGroups.value[groupIndex].model = newModel
      }
    }

    // 初始化
    const initGroups = () => {
      if (props.defaultGroup && props.groupsModel.length < 1) {
        addGroup()
      }
    }

    initGroups()

    return {
      t,
      fM,
      fS,
      fR,
      cFormRef,
      formGroups,
      groupFields,
      groupFormRules,
      updateGroupModel,
      addGroup,
      removeGroup,
      setGroupFormRef,
      submitEmit,
      resetEmit
    }
  }
})
